import { Button } from '@creditornot/cb-button';
import { useDirection } from '@creditornot/cb-provider';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { DecodedValueMap } from 'use-query-params';

import { fetchItemsTotalsCsvV2, fetchPurchaseCsvV2 } from 'apis/merchant-gateway/merchant-analytics/api';
import { isAnalyticsCSVFilter } from 'apis/merchant-gateway/merchant-analytics/utils';
import { typographyCss } from 'common/typography';
import { downloadFileFromAxiosResponse } from 'common/utils/download';
import { telemetryMerchantAdminPurchasesInteractionDownloadCsvConfirmed } from 'modules/purchases/telemetry';

import { IsoDateRange } from './FilterDateRangePicker';
import { PurchasesQueryConfig } from './purchasesQueryConfig';

type DownloadCSVProps = {
  className?: string;
  venueId: string;
  timezone: string;
  dateRange: IsoDateRange;
  query: DecodedValueMap<PurchasesQueryConfig>;
  isDownloadingDisabled: boolean;
  onClose: () => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h2`
  ${typographyCss.Heading6}
  margin: 0;
`;

const Explanation = styled.p`
  ${typographyCss.Body3}
  margin-block: 0;

  li {
    margin-inline-start: 8px;
  }

  li + li {
    margin-block-end: 8px;
  }
`;

const DownloadCSV = ({
  className,
  venueId,
  timezone,
  dateRange,
  query,
  isDownloadingDisabled,
  onClose,
}: DownloadCSVProps) => {
  const { locale } = useIntl();
  const cbDirection = useDirection();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleCSVDownload = async (): Promise<void> => {
    if (!(dateRange && dateRange.to && dateRange.from) || isDownloading) {
      return;
    }
    setIsDownloading(true);
    const params = {
      venueId,
      start_time: DateTime.fromISO(dateRange.from, { zone: timezone }).toISODate(),
      end_time: DateTime.fromISO(dateRange.to, { zone: timezone }).plus({ days: 1 }).toISODate(),
      locale,
      ...(query && query.filters && { filters: query.filters.split(',').filter(isAnalyticsCSVFilter) }),
    };

    try {
      const downloadedPurchasesCSV = await (query.view === 'items'
        ? fetchItemsTotalsCsvV2(params)
        : fetchPurchaseCsvV2(params));
      downloadFileFromAxiosResponse(downloadedPurchasesCSV);
      telemetryMerchantAdminPurchasesInteractionDownloadCsvConfirmed();
      onClose();
    } catch (e) {
      toast.error(<FormattedMessage id="global-error-message.header" />);
      onClose();
    }
  };

  return (
    <Root className={className} dir={cbDirection}>
      <Title>
        <FormattedMessage id="purchases.download-csv" />
      </Title>
      <Explanation>
        <FormattedMessage id="purchases.download-csv-help-text" values={{ li: chunks => <li>{chunks}</li> }} />
      </Explanation>
      <Button size="small" variant="primary" onClick={handleCSVDownload} disabled={isDownloadingDisabled}>
        {isDownloading ? (
          <FormattedMessage id="purchases.downloading" />
        ) : (
          <FormattedMessage id="purchases.download-csv" />
        )}
      </Button>
      <Button size="small" variant="secondary" onClick={() => onClose()}>
        <FormattedMessage id="state.cancel" />
      </Button>
    </Root>
  );
};

export default DownloadCSV;
